<template>
    <el-drawer title="Educations" v-model="localEducationDialogVisible" :append-to-body="true" :direction="'btt'"
        :destroy-on-close="true">
        <el-form :model="editEducationForm" ref="editForm">
            <el-form-item>
                <div v-for="(edu, index) in educations" :key="index" class="education-input text-left">
                    <div class="full-width">
                        <el-input v-model="edu.school" placeholder="School" @blur="edu.dirty.school = true"
                            maxlength="150"
                            show-word-limit
                            :error="$v.educations.$each.$response.$data[index].school.$error"
                            :class="{ 'error':edu.dirty?.school && $v.educations.$each.$response.$data[index].school.$error }"></el-input>
                        <el-form-item-error v-if="edu.dirty?.school && $v.educations.$each.$response.$data[index].school.$error">
                            {{ $v.educations.$each.$response.$errors[index].school[0].$message }}
                        </el-form-item-error>
                    </div>
                    <div class="full-width">
                        <el-input v-model="edu.title" placeholder="Title" @blur="edu.dirty.title = true"
                            maxlength="150"
                            show-word-limit
                            :error="$v.educations.$each.$response.$data[index].title.$error"
                            :class="{ 'error':edu.dirty?.title && $v.educations.$each.$response.$data[index].title.$error }"></el-input>
                        <el-form-item-error v-if="edu.dirty?.title && $v.educations.$each.$response.$data[index].title.$error">
                            {{ $v.educations.$each.$response.$errors[index].title[0].$message }}
                        </el-form-item-error>
                    </div>
                    <div>
                        <el-date-picker v-model="edu.dateStart" type="date" placeholder="Select start date" @blur="edu.dirty.dateStart = true"
                            :format="$filters.systemDateFormat()" :editable="true" value-format="YYYY-MM-DD"
                            :error="$v.educations.$each.$response.$data[index].dateStart.$error"
                            :class="{ 'error': edu.dirty?.dateStart && $v.educations.$each.$response.$data[index].dateStart.$error }"
                            @change="handleDateChange(edu, 'dateStart',index)"
                            @clear="handleDateClear(edu, 'dateStart',index)">>
                        </el-date-picker>
                        <el-form-item-error v-if=" edu.dirty?.dateStart && $v.educations.$each.$response.$data[index].dateStart.$error">
                            {{ $v.educations.$each.$response.$errors[index].dateStart[0].$message }}
                        </el-form-item-error>
                    </div>
                    <div>
                        <el-date-picker v-model="edu.dateEnd" type="date" placeholder="Select end date"  @blur="edu.dirty.dateEnd = true"
                            :format="$filters.systemDateFormat()" :editable="true" value-format="YYYY-MM-DD"
                            :error="$v.educations.$each.$response.$data[index].dateEnd.$error"
                            :class="{ 'error':edu.dirty?.dateEnd && $v.educations.$each.$response.$data[index].dateEnd.$error }"
                            @change="handleDateChange(edu, 'dateEnd',index)"
                            @clear="handleDateClear(edu, 'dateEnd',index)">
                        </el-date-picker>
                        <el-form-item-error v-if="edu.dirty?.dateEnd && $v.educations.$each.$response.$data[index].dateEnd.$error">
                            {{ $v.educations.$each.$response.$errors[index].dateEnd[0].$message }}
                        </el-form-item-error>
                    </div>
                    <el-button type="danger" @click="removeEducation(index)">
                        <TrashIcon />
                    </el-button>
                </div>
                <el-button type="primary" @click="addEducation" class="add-education-btn">Add</el-button>
            </el-form-item>
        </el-form>
        <div slot="footer" class="d-flex gap-1 my-3">
            <el-button type="primary" class="mt-0" @click="saveEducations"
                :disabled="isSaveButtonDisabled">Save</el-button>
            <el-button class="secondary mt-0" @click="closeEducationDialog">Cancel</el-button>
        </div>
    </el-drawer>
</template>
<script setup>
import { ref, reactive, defineProps, defineEmits, watch, toRefs, computed, onBeforeMount } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useVuelidate } from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import TrashIcon from '@/components/Icons/TrashIcon.vue'
import { isDateValid } from '@/helpers/date/dateHelpers'

// Store and Props
const store = useStore();
const userData = ref(store.getters.GET_USER_DATA ? store.getters.GET_USER_DATA : {});
const userRole = userData.value.role;

const userEducations = ref(
    userRole === 'locum' 
        ? userData.value.locumAsJobSeekerSetting?.hrFlowData?.educations 
        : userData.value.settings?.hrFlowData?.educations || []
);

const props = defineProps({
    educationDialogVisible: {
        type: Boolean,
        required: false
    }
});

// Emit event
const emit = defineEmits(['closeEducationDrawer']);

// Reactive Form Data
const editEducationForm = reactive({
    educations: []
});

// Break the reactivity chain to avoid constructor error
const { educations } = toRefs(editEducationForm);

const formatDate = (dateStr) => {
    //if empty return empty string
    if (!dateStr) {
        return '';
    }
    if (dateStr.includes("T")) {
        return dateStr.split("T")[0];
    } else {
        if (isDateValid(dateStr)) {
            return dateStr;
        }
        return '';
    }
}

// Fetch user data when drawer is opened, added this to fix the issue of not updating the educations list after adding a new experience
const fetchUserData = async () => {
    try {
        userData.value = store.getters.GET_USER_DATA ? store.getters.GET_USER_DATA : {};
        userEducations.value = userRole === 'locum' 
            ? userData.value.locumAsJobSeekerSetting?.hrFlowData?.educations
            : userData.value.settings?.hrFlowData?.educations || [];
        if (userEducations.value?.length > 0) {
            userEducations.value = userEducations.value?.map(edu => ({
                ...edu,
                dateStart: formatDate(edu.dateStart),
                dateEnd: formatDate(edu.dateEnd),
                dirty: { school: false,title:false, dateStart: false, dateEnd: false }
            }));
        }

        // Deep copy the updated educations into the edit form
        editEducationForm.educations = userEducations.value?.map(edu => ({ ...edu }));

        console.log("Educations", editEducationForm)
    } catch (error) {
        ElMessage({ type: 'error', message: error.message });
    }
};

// Validation rules
const rules = computed(() => {
    return {
        educations: {
            $each: helpers.forEach({
                school: {
                    required: helpers.withMessage('School is required', value => value.trim() !== '')
                },
                title: {
                    required: helpers.withMessage('Title is required', value => value.trim() !== '')
                },
                dateStart: {
                    required: helpers.withMessage('Start date is required', value => value.trim() !== '')
                },
                dateEnd: {
                    required: helpers.withMessage('End date is required', value => value.trim() !== ''),
                    custom: helpers.withMessage('End date must be greater than start date', (value, parent) => {
                        if (value && parent.dateStart) {
                            return new Date(value) > new Date(parent.dateStart);
                        }
                        return true;
                    })
                },
            })
        }
    };
});

// Initialize Vuelidate
const $v = useVuelidate(rules, editEducationForm);

// Watchers to Sync Drawer Visibility
const localEducationDialogVisible = ref(props.educationDialogVisible);

watch(localEducationDialogVisible, (newVal) => {
    if (!newVal) closeEducationDialog();
});

watch(() => props.educationDialogVisible, (newVal) => {
    localEducationDialogVisible.value = newVal;
    // if newVal is true, get updated user data and set educations
    if (newVal) {
        fetchUserData();
    }
});

onBeforeMount(() => {
    fetchUserData();
});

// Functions
const addEducation = () => {
    if (!editEducationForm.educations) {
        editEducationForm.educations = []; // Ensure array exists before pushing
    }
    educations.value.push({ school: '', title: '', dateStart: '', dateEnd: '', dirty: { school: false,title:false, dateStart: false, dateEnd: false } });
    
};

const removeEducation = (index) => {
    educations.value.splice(index, 1);
};

const isSaveButtonDisabled = computed(() => {
    return $v.value.$pending || $v.value.$error || $v.value.$invalid;
});

const saveEducations = async () => {
    try {
        await ElMessageBox.confirm(
            `Are you sure you want to update educations?`,
            "Update Educations",
            {
                confirmButtonText: "Update",
                cancelButtonText: "Cancel",
                confirmButtonClass: "el-button--primary",
                cancelButtonClass: "el-button--grey",
                showClose: false,


            }
        );

        await (async () => {
            try {
                let settingId = null;
                if(userRole === 'locum') {
                    settingId = userData.value.locumAsJobSeekerSetting.id;
                } else {
                    settingId = userData.value.settings.id;
                }
                const response = await store.dispatch("UPDATE_JOB_SEEKER_EDUCATIONS", { params: editEducationForm, id: settingId });

                store.dispatch("SET_CURRENT_USER_DATA", {
                    with:
                        "settings,billingInfo,schedule,bankVerification,address,push_notifications,config.hours_before_shift_lock",
                });
                if (response.status) {
                    ElMessage({
                        type: "success",
                        message: response.message,
                    });

                } else {
                    ElMessage({
                        type: "info",
                        message: response.message,
                    });
                }
            } catch (error) {
                if (error.message) {
                    ElMessage({ type: 'error', message: error.message });
                }
            }
            closeEducationDialog();
        })();

    } catch (error) {
        console.log(error);
    }
};

const handleDateChange = (education, field,index) => {
  if (!education[field]) {
    education[field] = ""; // Prevent it from becoming null
    educations.value[index]['dirty'][field] = true;
  }
};

const handleDateClear = (education, field,index) => {
  educations[field] = ""; // Keep it as an empty string instead of removing fields
  educations.value[index]['dirty'][field] = true;
};

const closeEducationDialog = () => {
    educations.value = [];
    emit('closeEducationDrawer', false);
};
</script>
<style lang="scss">
.el-overlay.is-message-box,
.el-message.el-message--error {
    z-index: 9999 !important;
}
</style>